import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, TextField, MenuItem, Select, FormControl, InputLabel,
  Pagination, CircularProgress, Alert, InputAdornment, Chip, Button,
} from '@mui/material';
import { Search, Edit as EditIcon } from '@mui/icons-material';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import ArticleCard from '../components/Blog/ArticleCard';
import SEOHead from '../components/SEO/SEOHead';
import BlogService, { ListArticlesParams } from '../services/blog-service';
import { BlogArticle, ArticleCategory } from '../types/blog';
import { useAuth } from '../contexts/AuthContext';

const CATEGORIES: ArticleCategory[] = [
  'Styling Tips', 'Trend Roundups', 'How to Style',
  'Fashion Advice', 'Seasonal Fashion', 'Street Style',
];

const BlogListPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [category, setCategory] = useState<string>(searchParams.get('category') || '');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [tag, setTag] = useState(searchParams.get('tag') || '');

  const fetchArticles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: ListArticlesParams = { page, limit: 12, status: 'published' };
      if (category) params.category = category as ArticleCategory;
      if (search) params.search = search;
      if (tag) params.tag = tag;
      const data = await BlogService.listArticles(params);
      setArticles(data.articles || []);
      setTotalPages(data.pagination?.totalPages || 1);
    } catch {
      setError('Failed to load articles.');
    } finally {
      setLoading(false);
    }
  }, [page, category, search, tag]);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  useEffect(() => {
    const p = new URLSearchParams();
    if (page > 1) p.set('page', page.toString());
    if (category) p.set('category', category);
    if (search) p.set('search', search);
    if (tag) p.set('tag', tag);
    setSearchParams(p, { replace: true });
  }, [page, category, search, tag, setSearchParams]);

  const handleSearchSubmit = (e: React.FormEvent) => { e.preventDefault(); setPage(1); };

  return (
    <Box>
      <SEOHead
        title="Fashion Blog"
        description="Discover the latest fashion tips, styling guides, and trend roundups on Hooliah."
        canonicalUrl="https://hooliah.com/blog"
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700 }}>Fashion Blog</Typography>
        {isAuthenticated && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            component={RouterLink}
            to="/blog/new"
          >
            Write Article
          </Button>
        )}
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Tips, trends, and styling inspiration from the Hooliah community.
        {isAuthenticated && ' Share your fashion knowledge — write an article!'}
      </Typography>

      {/* Filters */}
      <Box component="form" onSubmit={handleSearchSubmit} sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search articles…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Category</InputLabel>
          <Select value={category} label="Category" onChange={(e) => { setCategory(e.target.value); setPage(1); }}>
            <MenuItem value="">All Categories</MenuItem>
            {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {tag && (
        <Box sx={{ mb: 2 }}>
          <Chip label={`Tag: ${tag}`} onDelete={() => { setTag(''); setPage(1); }} color="primary" variant="outlined" />
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : articles.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No articles found.</Typography>
      ) : (
        <>
          <Grid container spacing={3}>
            {articles.map((article) => (
              <Grid item xs={12} sm={6} md={4} key={article.articleId}>
                <ArticleCard article={article} />
              </Grid>
            ))}
          </Grid>
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default BlogListPage;
